import React, { useCallback, useEffect, useRef } from 'react';

import { AgentUploadLoading } from '@/components/Common/Loader';
import { UploadStatus, VideoFailedReason } from '@/types/components/Video';
import { retryForCompressFailed, retryForGetTokenFailed, retryForSendingMessageFailed, retryForUploadFailed } from '@/utils/videoTools';
import { inject, observer } from 'mobx-react';
import { AgentStore } from '@/stores/agent';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { FAKE_SESSION_ID, TRACKING_TYPE } from '@/utils/constants';
import { trackingHandler } from '@/utils/btnClickHandler';

import imageFailure from '@/assets/img/image_failure.svg';
import clsx from 'classnames';
import { getChildInject } from '@/utils/instantLoaded/tools';

export interface VideoStatusProps {
  /** 出错了 */
  error: boolean;
  /** 上传 & 发送消息成功 */
  finish: boolean;
  /** 是否反转气泡 */
  reverse: boolean;
  /** 当前状态 */
  status: UploadStatus;
  /** video id */
  vid: string;
  /** 给后端发消息的参数 */
  sendMessageParams: any;
  /** 时长 */
  duration: number;
  beErrorCode?: number;
  curPos?: number;

  agentStore?: AgentStore;
  userStore?: any;
  initStore?: any;
}

const VideoStatus = (props: VideoStatusProps) => {

  const errorHandler = useCallback(() => {
    // 如果会话已经结束
    if (props!.agentStore!.agentEndedReason) {
      return;
    }

    if (typeof props.curPos === 'number') {
      // 点击重试后，去掉 beErrorCode
      props!.agentStore!.UPDATE_AGENT_DATA_ITEM({ beErrorCode: undefined }, props.curPos!);
    }

    switch (props.status) {
      case UploadStatus.GetTokenFailed:
        retryForGetTokenFailed(props.vid);
        return;
      case UploadStatus.CompressFailed:
        retryForCompressFailed();
        return;
      case UploadStatus.UploadFailed:
        retryForUploadFailed(props.vid);
        return;
      case UploadStatus.SendMessageFailed:
        retryForSendingMessageFailed(props.vid, props.sendMessageParams);
        return;
      case UploadStatus.UploadCanceled:
        retryForCompressFailed();
        return;
      default:
        return;
    }
  }, [props.status]);

  // @ts-ignore
  const failReason = VideoFailedReason[props.status] || 0;

  if (props.finish) {
    return null;
  } else if (props.error) {
    return (
      <VideoStatusError
        beErrorCode={props.beErrorCode}
        sessionId={props.initStore.sessionId}
        reverse={props.reverse}
        duration={props.duration}
        failReason={failReason}
        loyaltyTierCode={props.userStore.loyaltyTierCode}
        errorHandler={errorHandler}
      />
    );
  }

  return (
    <div className={clsx('status-icon', { reverse: props.reverse })}>
      <AgentUploadLoading />
    </div>
  );
};

const VideoStatusError = (props: {
  reverse: boolean,
  loyaltyTierCode?: number,
  duration: number,
  failReason: number,
  sessionId?: string,
  beErrorCode?: number,
  errorHandler: () => void
}) => {
  const videoStatusRef = useRef(null);

  const trackingname = TRACKING_TYPE.VIDEO_RESEND;
  const trackingdata = {
    client_rank: props.loyaltyTierCode?.toString(),
    video_original_duration: props.duration,
    failure_reason: props.failReason,
    be_error_code: props.beErrorCode || undefined,
    current_chat_session_id: props.sessionId && props.sessionId !== FAKE_SESSION_ID ? props.sessionId : '0',
  };

  const handleErrorClick = () => {
    trackingHandler({
      trackingname,
      trackingdata,
    });

    props.errorHandler();
  };

  useEffect(() => {
    addObserver(videoStatusRef.current);

    return () => {
      removeObserver(videoStatusRef.current);
    };
  }, []);

  return (
    <div
      ref={videoStatusRef}
      data-trackingname={trackingname}
      data-trackingdata={JSON.stringify(trackingdata)}
      className={clsx('status-icon', { reverse: props.reverse })}
      onClick={handleErrorClick}
    >
      <img src={imageFailure} />
    </div>
  );
};

export default inject(getChildInject('initStore', 'agentStore', 'userStore'))(observer(VideoStatus));
